import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/UserAvatar';
import { toast } from 'sonner';
import {
  Search, History, ChevronLeft, ChevronRight, ShieldAlert, Siren,
  Lock, LockOpen, MapPin, Loader2, Phone, Copy,
} from 'lucide-react';
import { format } from 'date-fns';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'searching', label: 'Searching' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled_by_passenger', label: 'Cancelled (passenger)' },
  { value: 'canceled_by_driver', label: 'Cancelled (driver)' },
  { value: 'emergency_end', label: 'Emergency end' },
];

const STATUS_COLORS: Record<string, string> = {
  searching: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  accepted: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  in_progress: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  canceled_by_passenger: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  canceled_by_driver: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  emergency_end: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const STATUS_LABEL: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.filter((s) => s.value).map((s) => [s.value, s.label]),
);

const PAGE_SIZE = 50;

// ─── Types ───────────────────────────────────────────────────────────────────

interface RideRow {
  id: string;
  status: string;
  fare_xcd: number | null;
  seats_requested: number | null;
  requested_at: string;
  accepted_at: string | null;
  picked_up_at: string | null;
  completed_at: string | null;
  canceled_at: string | null;
  origin_name: string;
  destination_name: string;
  protected: boolean;
  passenger_id: string | null;
  passenger_name: string | null;
  passenger_phone: string | null;
  driver_id: string | null;
  driver_name: string | null;
  driver_phone: string | null;
  driver_plate: string | null;
  sos_count: number;
  incident_count: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function fmt(ts: string | null): string {
  if (!ts) return '–';
  try { return format(new Date(ts), 'dd MMM yyyy, HH:mm'); } catch { return ts; }
}

function copy(text: string | null, label: string) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error('Copy failed'),
  );
}

// ─── Detail drawer ─────────────────────────────────────────────────────────────

function RideDetailDrawer({
  rideId, open, onClose, onHoldChange,
}: {
  rideId: string | null;
  open: boolean;
  onClose: () => void;
  onHoldChange: (id: string, held: boolean) => void;
}) {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [holdSubmitting, setHoldSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !rideId) return;
    let active = true;
    setLoading(true);
    setDetail(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('admin_get_ride_detail', { p_ride_id: rideId });
        if (error) throw error;
        if (!active) return;
        if (data && (data as any).ok) {
          setDetail((data as any).ride);
        } else {
          toast.error((data as any)?.error === 'not_found' ? 'Ride not found' : 'Failed to load ride');
        }
      } catch (err: any) {
        if (active) toast.error(err.message || 'Failed to load ride');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, rideId]);

  const toggleHold = async () => {
    if (!rideId || !detail) return;
    const next = !detail.protected;
    setHoldSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('admin_set_ride_hold', { p_ride_id: rideId, p_hold: next });
      if (error) throw error;
      if (data && (data as any).ok) {
        setDetail((d: any) => ({ ...d, protected: next }));
        onHoldChange(rideId, next);
        toast.success(next ? 'Litigation hold placed. The ride can never be purged.' : 'Litigation hold released');
      } else {
        toast.error('Action failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Action failed');
    } finally {
      setHoldSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-[#0b0f17] border-gray-800 text-gray-100">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <History className="w-5 h-5 text-cyan-400" />
            Ride detail
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="space-y-3 mt-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 bg-gray-800/50 rounded" />
            ))}
          </div>
        ) : !detail ? (
          <div className="mt-10 text-center text-gray-500">No data.</div>
        ) : (
          <div className="space-y-6 mt-6 text-sm">
            {/* Status + hold */}
            <div className="flex items-center justify-between gap-3">
              <Badge className={`${STATUS_COLORS[detail.status] || 'bg-gray-700 text-gray-300'}`}>
                {STATUS_LABEL[detail.status] || detail.status}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                disabled={holdSubmitting}
                onClick={toggleHold}
                className={detail.protected
                  ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10'
                  : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
              >
                {holdSubmitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : detail.protected ? <Lock className="w-3.5 h-3.5 mr-1.5" />
                  : <LockOpen className="w-3.5 h-3.5 mr-1.5" />}
                {detail.protected ? 'Release litigation hold' : 'Place litigation hold'}
              </Button>
            </div>

            {detail.protected && (
              <p className="text-xs text-amber-400/80 -mt-3">
                This ride is on litigation hold, so it is exempt from the nightly purge and will be retained indefinitely.
              </p>
            )}

            {/* Ride id */}
            <div className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2">
              <span className="font-mono text-xs text-gray-400 truncate">{detail.id}</span>
              <button onClick={() => copy(detail.id, 'Ride ID')} className="text-gray-500 hover:text-cyan-300">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Timeline */}
            <Section title="Timeline">
              <Field label="Requested" value={fmt(detail.requested_at)} />
              <Field label="Accepted" value={fmt(detail.accepted_at)} />
              <Field label="Picked up" value={fmt(detail.picked_up_at)} />
              <Field label="Completed" value={fmt(detail.completed_at)} />
              <Field label="Cancelled" value={fmt(detail.canceled_at)} />
            </Section>

            {/* Trip */}
            <Section title="Trip">
              <Field label="From" value={detail.origin_name} />
              <Field label="To" value={detail.destination_name} />
              <Field label="Fare" value={detail.fare_xcd != null ? `EC$${Number(detail.fare_xcd).toFixed(2)}` : '–'} />
              <Field label="Seats" value={String(detail.seats_requested ?? 1)} />
              {detail.pickup_notes && <Field label="Pickup note" value={detail.pickup_notes} />}
              {detail.on_behalf_of_name && (
                <Field label="Booked for" value={`${detail.on_behalf_of_name}${detail.on_behalf_of_age_band ? ` (${detail.on_behalf_of_age_band})` : ''}`} />
              )}
            </Section>

            {/* GPS */}
            <Section title="GPS coordinates">
              <GpsRow label="Origin" lat={detail.origin_lat} lng={detail.origin_lng} />
              <GpsRow label="Passenger pickup" lat={detail.pickup_lat} lng={detail.pickup_lng} />
              <GpsRow label="Destination" lat={detail.destination_lat} lng={detail.destination_lng} />
            </Section>

            {/* Parties */}
            <Section title="Passenger">
              <Party
                name={detail.passenger?.full_name}
                phone={detail.passenger?.phone_number}
                pic={detail.passenger?.profile_picture_url}
                sub={detail.passenger?.id}
              />
            </Section>
            <Section title="Driver">
              {detail.driver ? (
                <Party
                  name={detail.driver.full_name}
                  phone={detail.driver.phone_number}
                  pic={detail.driver.profile_picture_url}
                  sub={[detail.driver.license_plate, detail.driver.vehicle_color, detail.driver.vehicle_make, detail.driver.vehicle_model].filter(Boolean).join(' · ')}
                />
              ) : (
                <p className="text-gray-500">No driver was ever assigned to this ride.</p>
              )}
            </Section>

            {/* SOS events */}
            <Section title={`SOS events (${detail.sos_events?.length || 0})`}>
              {(!detail.sos_events || detail.sos_events.length === 0) ? (
                <p className="text-gray-500">None.</p>
              ) : (
                detail.sos_events.map((s: any) => (
                  <div key={s.id} className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 space-y-1">
                    <div className="flex items-center gap-2 text-red-300">
                      <Siren className="w-3.5 h-3.5" />
                      <span className="font-medium">{fmt(s.created_at)}</span>
                      <Badge className="bg-red-500/10 text-red-300 border-red-500/20 text-[10px]">{s.role}</Badge>
                    </div>
                    <GpsRow label="At" lat={s.latitude} lng={s.longitude} />
                    {s.payload?.mode && <Field label="Mode" value={String(s.payload.mode)} />}
                  </div>
                ))
              )}
            </Section>

            {/* Incidents */}
            <Section title={`Incident reports (${detail.incidents?.length || 0})`}>
              {(!detail.incidents || detail.incidents.length === 0) ? (
                <p className="text-gray-500">None.</p>
              ) : (
                detail.incidents.map((i: any) => (
                  <div key={i.id} className="rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-gray-300 font-medium">{i.category}</span>
                      <Badge className="bg-gray-700/50 text-gray-300 border-gray-600 text-[10px]">{i.severity}</Badge>
                      <Badge className="bg-gray-700/50 text-gray-300 border-gray-600 text-[10px]">{i.status}</Badge>
                    </div>
                    <p className="text-gray-400 text-xs">{i.description}</p>
                    <p className="text-gray-600 text-[10px]">{fmt(i.created_at)}</p>
                  </div>
                ))
              )}
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-200 text-right break-words max-w-[60%]">{value}</span>
    </div>
  );
}

function GpsRow({ label, lat, lng }: { label: string; lat: number | null; lng: number | null }) {
  if (lat == null || lng == null) return <Field label={label} value="–" />;
  const text = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <a
        href={`https://www.google.com/maps?q=${lat},${lng}`}
        target="_blank"
        rel="noreferrer"
        className="text-cyan-400 hover:underline font-mono text-xs flex items-center gap-1"
      >
        <MapPin className="w-3 h-3" />{text}
      </a>
    </div>
  );
}

function Party({ name, phone, pic, sub }: { name?: string | null; phone?: string | null; pic?: string | null; sub?: string | null }) {
  return (
    <div className="flex items-center gap-3">
      <UserAvatar size="md" name={name || 'Unknown'} src={pic ?? null} />
      <div className="flex flex-col min-w-0">
        <span className="text-white font-medium">{name || 'Unknown'}</span>
        {phone && (
          <a href={`tel:${phone.replace(/\s+/g, '')}`} className="text-gray-400 text-xs font-mono hover:underline flex items-center gap-1">
            <Phone className="w-3 h-3" />{phone}
          </a>
        )}
        {sub && <span className="text-gray-500 text-xs truncate">{sub}</span>}
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function RideHistory() {
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rides, setRides] = useState<RideRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const [selectedRideId, setSelectedRideId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchRides = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_search_rides', {
        p_query: submittedQuery || null,
        p_status: status || null,
        p_from: from ? new Date(from).toISOString() : null,
        p_to: to ? new Date(to + 'T23:59:59').toISOString() : null,
        p_limit: PAGE_SIZE,
        p_offset: page * PAGE_SIZE,
      });
      if (error) throw error;
      if (data && (data as any).ok) {
        setRides((data as any).rides ?? []);
        setTotal((data as any).total ?? 0);
      } else {
        toast.error((data as any)?.error === 'forbidden' ? 'Admin access required' : 'Search failed');
        setRides([]);
        setTotal(0);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to search rides');
    } finally {
      setLoading(false);
    }
  }, [submittedQuery, status, from, to, page]);

  useEffect(() => { fetchRides(); }, [fetchRides]);

  const runSearch = () => {
    setPage(0);
    setSubmittedQuery(query.trim());
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleHoldChange = (id: string, held: boolean) => {
    setRides((prev) => prev.map((r) => (r.id === id ? { ...r, protected: held } : r)));
  };

  return (
    <TooltipProvider>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <History className="w-6 h-6 text-cyan-400" />
            Ride History
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Read-only forensic lookup of every ride for disputes, safety reviews, and assisting authorities. Place a litigation hold to retain a ride indefinitely.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-gray-500 uppercase tracking-wide">Search</label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <Input
                placeholder="Ride ID, name, phone, or town…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                className="pl-8 h-9 text-sm bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-500"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide">Status</label>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(0); }}
              className="mt-1 block bg-gray-900/50 border border-gray-800 rounded-md px-2 h-9 text-sm text-gray-300"
            >
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide">From</label>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }}
              className="mt-1 h-9 text-sm bg-gray-900/50 border-gray-800 text-white w-[150px]" />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide">To</label>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }}
              className="mt-1 h-9 text-sm bg-gray-900/50 border-gray-800 text-white w-[150px]" />
          </div>
          <Button onClick={runSearch} className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white">
            <Search className="w-4 h-4 mr-1.5" /> Search
          </Button>
        </div>

        {/* Table */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 bg-gray-800/50 rounded" />
                ))}
              </div>
            ) : rides.length === 0 ? (
              <div className="p-16 text-center">
                <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 font-medium">No rides match your search</p>
                <p className="text-gray-500 text-sm mt-1">Try a ride ID, a passenger/driver name, a phone number, or widen the date range.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400 w-32">Requested</TableHead>
                    <TableHead className="text-gray-400 w-28">Status</TableHead>
                    <TableHead className="text-gray-400">Passenger</TableHead>
                    <TableHead className="text-gray-400">Driver</TableHead>
                    <TableHead className="text-gray-400">Route</TableHead>
                    <TableHead className="text-gray-400 w-20 text-right">Fare</TableHead>
                    <TableHead className="text-gray-400 w-24 text-center">Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rides.map((r) => (
                    <TableRow
                      key={r.id}
                      className="border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                      onClick={() => { setSelectedRideId(r.id); setDrawerOpen(true); }}
                    >
                      <TableCell className="text-gray-400 text-xs">{fmt(r.requested_at)}</TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[r.status] || 'bg-gray-700 text-gray-300'} text-xs`}>
                          {STATUS_LABEL[r.status] || r.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-white text-sm">{r.passenger_name || 'Unknown'}</span>
                          {r.passenger_phone && <span className="text-gray-500 text-xs font-mono">{r.passenger_phone}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-white text-sm">{r.driver_name || '–'}</span>
                          {r.driver_plate && <span className="text-gray-500 text-xs font-mono">{r.driver_plate}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300 text-sm">
                        <span className="text-gray-400">{r.origin_name}</span>
                        <span className="text-gray-600"> → </span>
                        <span className="text-gray-400">{r.destination_name}</span>
                      </TableCell>
                      <TableCell className="text-right text-gray-300 text-sm">
                        {r.fare_xcd != null ? `EC$${Number(r.fare_xcd).toFixed(2)}` : '–'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1.5">
                          {r.sos_count > 0 && (
                            <Tooltip>
                              <TooltipTrigger><Siren className="w-3.5 h-3.5 text-red-400" /></TooltipTrigger>
                              <TooltipContent className="bg-gray-900 border-gray-700 text-white text-xs">{r.sos_count} SOS event(s)</TooltipContent>
                            </Tooltip>
                          )}
                          {r.incident_count > 0 && (
                            <Tooltip>
                              <TooltipTrigger><ShieldAlert className="w-3.5 h-3.5 text-amber-400" /></TooltipTrigger>
                              <TooltipContent className="bg-gray-900 border-gray-700 text-white text-xs">{r.incident_count} incident report(s)</TooltipContent>
                            </Tooltip>
                          )}
                          {r.protected && (
                            <Tooltip>
                              <TooltipTrigger><Lock className="w-3.5 h-3.5 text-amber-300" /></TooltipTrigger>
                              <TooltipContent className="bg-gray-900 border-gray-700 text-white text-xs">On litigation hold</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {!loading && rides.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="text-gray-400 hover:text-white">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-gray-400">Page {page + 1} of {totalPages || 1}</span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="text-gray-400 hover:text-white">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <RideDetailDrawer
          rideId={selectedRideId}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onHoldChange={handleHoldChange}
        />
      </div>
    </TooltipProvider>
  );
}
